import React, { ReactElement, useCallback, useEffect, useState, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  Grid2,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useProxyAccessControl } from '../../../../hooks/useProxyAccessControl';
import {
  white,
  gray100,
  gray40,
  gray80,
  gray60,
  primaryButtonBackgroundColor,
  buttonTextIconLarge,
  white_overlay80,
} from '../../../../constants/colors';
import { boldXxl, regularSm, spHeaderTitle } from '../../../../constants/location';
import SideBar from '../../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import MetaTitle from '../../../utils/MetaTitle';
import CompanyListCard from '../../../uiParts/card/CompanyListCard';
import {
  getAllCompanies,
  getTotalUsageCountByCompanyId,
} from '../../../../api/firebase/firestore/company/read';
import { getPatternCountByCompanyId } from '../../../../api/firebase/firestore/pattern/read';
import { CompanyType } from '../../../../types/company';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import { companyPlan } from '../../../../constants/enum';
import {
  COMPANY_LIST_TITLE,
  COMPANY_LIST_REGISTRATION_COUNT,
  COMPANY_LIST_REGISTRATION_COUNT_ALL,
  COMPANY_LIST_INVITE_BUTTON,
  COMPANY_LIST_SEARCH_PLACEHOLDER,
  COMPANY_LIST_PLAN_SELECT_PLACEHOLDER,
  COMPANY_LIST_CLEAR_BUTTON,
  COMPANY_LIST_ERROR_FETCH,
  COMPANY_PLAN_FREE,
  COMPANY_PLAN_STANDARD,
  COMPANY_PLAN_PRO,
  COMPANY_PLAN_CUSTOM,
} from '../../../../constants/labels';
import { AuthContext } from '../../../../contexts/auth';
import { useAuthRedirect } from '../../../../hooks/useAuthRedirect';
import { isOperatorStaff } from '../../../../utils/permission';

const inputBaseStyle = {
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: gray60,
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: gray100,
  },
};

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  headerRightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
      gap: '16px',
    },
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  registrationStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '@media screen and (max-width:960px)': {
      display: 'none',
    },
  },
  registrationStatusSp: {
    display: 'none',
    '@media screen and (max-width:960px)': {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
  },
  registrationStatusLabel: {
    ...regularSm,
    color: gray80,
  },
  registrationStatusValue: {
    ...regularSm,
    color: gray100,
  },
  buttonLarge: {
    height: '44px',
    padding: '16px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 8px)',
    },
    '@media screen and (max-width:600px)': {
      width: '100%',
    },
  },
  icon24: {
    height: '24px',
    marginLeft: '0px',
    width: '24px',
  },
  searchBox: {
    display: 'flex',
    gap: '16px',
    padding: '16px',
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    backgroundColor: white,
    flexWrap: 'wrap',
    margin: '0 32px',
    width: 'calc(100% - 64px)',
    boxSizing: 'border-box',
    '@media (max-width: 960px)': {
      margin: '0 16px',
      width: 'calc(100% - 32px)',
    },
  },
  searchField: {
    minWidth: '340px',
    textAlign: 'left',
    '@media screen and (max-width:960px)': {
      minWidth: '100%',
    },
    ...inputBaseStyle,
  },
  filterSelect: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
      borderWidth: '1px',
    },
    minWidth: '340px',
    textAlign: 'left',
    '@media screen and (max-width:960px)': {
      minWidth: '100%',
    },
  },
  clearButton: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid ${gray60}`,
      boxShadow: 'none',
      color: gray60,
      opacity: '80%',
    },
    backgroundColor: white,
    border: `1px solid ${gray80}`,
    boxShadow: 'none',
    color: gray80,
    alignSelf: 'flex-end',
    marginLeft: 'auto',
  },
  tableWrapper: {
    margin: '0 32px',
    width: 'calc(100% - 64px)',
    boxSizing: 'border-box',
    '@media (max-width: 960px)': {
      margin: '0 16px',
      width: 'calc(100% - 32px)',
    },
  },
};

const CompanyList = (): ReactElement => {
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { user, isLoading: isAuthLoading } = useContext(AuthContext);
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<CompanyType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorText, setErrorText] = useState<string>('');
  const [infoText] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [planFilter, setPlanFilter] = useState<number | string>('all');

  // 代理モード中はアクセス禁止
  useProxyAccessControl();

  // 運営スタッフ以外はアクセス不可
  useEffect(() => {
    // 認証ロード中は何もしない
    if (isAuthLoading) {
      return;
    }

    // ユーザーが存在しない場合はログイン画面へ
    if (!user) {
      return;
    }

    // 運営スタッフでない場合はホームへリダイレクト
    if (!isOperatorStaff(user.role)) {
      navigate('/home');
      return;
    }
  }, [user, isAuthLoading, navigate]);

  const fetchCompanies = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const companiesData = await getAllCompanies();

      // 各企業の利用枚数を取得（ユーザー数・読取パターン数はcompaniesから取得）
      const formattedData: CompanyType[] = await Promise.all(
        companiesData.map(async company => {
          // company_idに紐づく総利用枚数・読取パターン数を取得
          const [totalOcrCount, patternCount] = await Promise.all([
            getTotalUsageCountByCompanyId(company.company_id),
            getPatternCountByCompanyId(company.company_id),
          ]);

          return {
            company_id: company.company_id,
            company_name: company.company_name || '',
            plan: company.plan || 0,
            status: company.status ?? 1,
            total_ocr_count: totalOcrCount,
            current_pattern_count: patternCount,
            current_user_count: company.current_user_count ?? 0,
            created_at: company.created_at?.toDate ? company.created_at.toDate() : new Date(),
            invited_email: company.invited_email,
          };
        }),
      );

      setCompanies(formattedData);
      setFilteredCompanies(formattedData);
    } catch (error) {
      setErrorText(COMPANY_LIST_ERROR_FETCH);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 認証ロード中、またはユーザーが運営スタッフでない場合はデータ取得しない
    if (isAuthLoading || !user || !isOperatorStaff(user.role)) {
      return;
    }
    void fetchCompanies();
  }, [user, isAuthLoading, fetchCompanies]);

  useEffect(() => {
    let filtered = companies;

    // 検索フィルター
    if (searchText) {
      filtered = filtered.filter(company =>
        company.company_name.toLowerCase().includes(searchText.toLowerCase()),
      );
    }

    // プランフィルター
    if (planFilter !== 'all') {
      filtered = filtered.filter(company => company.plan === planFilter);
    }

    setFilteredCompanies(filtered);
  }, [searchText, planFilter, companies]);

  // 検索クリア関数
  const handleClearSearch = (): void => {
    setSearchText('');
    setPlanFilter('all');
  };

  // 企業を招待するボタンのクリックハンドラー
  const handleInviteCompany = (): void => {
    navigate('/admin/company/invite');
  };

  // 企業行クリックハンドラー
  const handleRowClick = (companyId: string, _companyName: string): void => {
    navigate(`/admin/company/${companyId}`);
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            '@media screen and (max-width:960px)': {
              paddingBottom: '84px',
            },
          },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Typography sx={styles.headerTitle}>{COMPANY_LIST_TITLE}</Typography>
          <Box sx={styles.registrationStatusSp}>
            <Typography sx={styles.registrationStatusValue}>{companies.length}</Typography>
            <Typography sx={styles.registrationStatusValue}>
              {COMPANY_LIST_REGISTRATION_COUNT_ALL}
            </Typography>
          </Box>
          <Box sx={styles.headerRightSection}>
            <Box sx={styles.registrationStatus}>
              <Typography sx={styles.registrationStatusValue}>
                {COMPANY_LIST_REGISTRATION_COUNT}
              </Typography>
              <Typography sx={styles.registrationStatusValue}>
                {filteredCompanies.length === companies.length
                  ? `${companies.length} ${COMPANY_LIST_REGISTRATION_COUNT_ALL}`
                  : `${filteredCompanies.length} ${COMPANY_LIST_REGISTRATION_COUNT_ALL} / ${companies.length} ${COMPANY_LIST_REGISTRATION_COUNT_ALL}`}
              </Typography>
            </Box>
            <Button
              onClick={handleInviteCompany}
              startIcon={<Add sx={styles.icon24} />}
              sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
            >
              <Typography sx={buttonTextIconLarge}>{COMPANY_LIST_INVITE_BUTTON}</Typography>
            </Button>
          </Box>
        </Box>

        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box sx={styles.searchBox}>
            <TextField
              sx={styles.searchField}
              placeholder={COMPANY_LIST_SEARCH_PLACEHOLDER}
              value={searchText}
              onChange={(e): void => setSearchText(e.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
            <Select
              sx={styles.filterSelect}
              value={planFilter}
              onChange={(e): void => setPlanFilter(e.target.value)}
              size="small"
              displayEmpty
              renderValue={(selected: string | number): React.ReactNode => {
                if (selected === 'all') {
                  return (
                    <span style={{ color: gray60 }}>{COMPANY_LIST_PLAN_SELECT_PLACEHOLDER}</span>
                  );
                }
                const planLabels: Record<string | number, string> = {
                  [companyPlan.free]: COMPANY_PLAN_FREE,
                  [companyPlan.standard]: COMPANY_PLAN_STANDARD,
                  [companyPlan.pro]: COMPANY_PLAN_PRO,
                  [companyPlan.custom]: COMPANY_PLAN_CUSTOM,
                };
                return planLabels[selected];
              }}
            >
              <MenuItem value="all">{COMPANY_LIST_PLAN_SELECT_PLACEHOLDER}</MenuItem>
              <MenuItem value={companyPlan.free}>{COMPANY_PLAN_FREE}</MenuItem>
              <MenuItem value={companyPlan.standard}>{COMPANY_PLAN_STANDARD}</MenuItem>
              <MenuItem value={companyPlan.pro}>{COMPANY_PLAN_PRO}</MenuItem>
              <MenuItem value={companyPlan.custom}>{COMPANY_PLAN_CUSTOM}</MenuItem>
            </Select>
            <Button onClick={handleClearSearch} sx={styles.clearButton}>
              <Typography sx={{ fontSize: '14px' }}>{COMPANY_LIST_CLEAR_BUTTON}</Typography>
            </Button>
          </Box>

          <Box sx={styles.tableWrapper}>
            {loading ? (
              <CustomLoading loading={loading} />
            ) : (
              <CompanyListCard
                companyList={filteredCompanies}
                onRowClick={handleRowClick}
                onRefresh={fetchCompanies}
              />
            )}
          </Box>
        </Grid2>
      </Box>

      <CustomSnackbar
        text={errorText}
        infoText={infoText}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default CompanyList;
